import { useEffect, useMemo, useState } from 'react';
import { Description, Drawer, type OverlayTriggerState } from '@heroui/react';
import { FilterPopoverFooter } from '../../../components/ui/filter-popover-footer';
import {
  drawerBodyClassName,
  drawerFooterClassName,
  sideDrawerDialogClassName,
} from '../../../components/ui/side-drawer-classes';
import {
  countActiveFilters,
  createDefaultFilters,
  hasActiveFilters,
} from '../utils/available-spots-filter-utils';
import {
  AvailableSpotsFiltersForm,
  type AvailableSpotsFiltersState,
} from './available-spots-filters-form';

interface AvailableSpotsFiltersDrawerProps {
  state: OverlayTriggerState;
  appliedFilters: AvailableSpotsFiltersState;
  onApply: (filters: AvailableSpotsFiltersState) => void;
}

export function AvailableSpotsFiltersDrawer({
  state,
  appliedFilters,
  onApply,
}: AvailableSpotsFiltersDrawerProps) {
  const baselineFilters = useMemo(() => createDefaultFilters(), []);
  const [draftFilters, setDraftFilters] = useState(appliedFilters);

  const draftActive = hasActiveFilters(draftFilters, baselineFilters);
  const appliedActive = hasActiveFilters(appliedFilters, baselineFilters);

  useEffect(() => {
    if (state.isOpen) {
      setDraftFilters(appliedFilters);
    }
  }, [state.isOpen, appliedFilters]);

  const handleReset = () => {
    setDraftFilters(baselineFilters);
    onApply(baselineFilters);
    state.close();
  };

  const handleApply = () => {
    onApply(draftFilters);
    state.close();
  };

  return (
    <Drawer state={state}>
      <Drawer.Backdrop isDismissable>
        <Drawer.Content placement="right">
          <Drawer.Dialog className={sideDrawerDialogClassName}>
            <Drawer.Header>
              <Drawer.Heading>Filtros</Drawer.Heading>
              <Drawer.CloseTrigger />
            </Drawer.Header>

            <Drawer.Body className={drawerBodyClassName}>
              <Description>Refine a busca por período e tipo de uso.</Description>
              <AvailableSpotsFiltersForm filters={draftFilters} onChange={setDraftFilters} />
            </Drawer.Body>

            <Drawer.Footer className={drawerFooterClassName}>
              <FilterPopoverFooter
                showReset={appliedActive || draftActive}
                onReset={handleReset}
                onApply={handleApply}
              />
            </Drawer.Footer>
          </Drawer.Dialog>
        </Drawer.Content>
      </Drawer.Backdrop>
    </Drawer>
  );
}

export function useAvailableSpotsFiltersCount(filters: AvailableSpotsFiltersState): number {
  const baselineFilters = useMemo(() => createDefaultFilters(), []);
  return countActiveFilters(filters, baselineFilters);
}
